import { z } from 'zod';
import { isValidUserPassword } from 'src/utils/general';

/**
 * One schema per wizard step, mirroring src/pages/Register/schema.ts. Both are
 * factories because the messages are translated at build time of the form.
 */

const permissionLevelSchema = z.enum(['none', 'read', 'read_write']);

export const createProfileStepSchema = (t: (key: string) => string) =>
  z.object({
    // 64 is the backend's own limit (CreateUserRights.name, max_length=64);
    // without it the user gets a raw pydantic 422 after the last step.
    name: z.string().trim().min(1, t('general.name_required')).max(64, t('general.name_too_long')),
    permissions: z.object({
      outbound: permissionLevelSchema,
      products: permissionLevelSchema,
      returns: permissionLevelSchema,
      carrier: permissionLevelSchema,
      integrations: permissionLevelSchema,
      cases: permissionLevelSchema,
    }),
  });

export const createCredentialsStepSchema = (t: (key: string) => string) =>
  z.object({
    // Deliberately no .max() here: unlike `name`, CreateUserRights.email carries
    // only a pattern, and the derived `login` is built in python without going
    // through pydantic — a length cap would reject addresses the backend accepts.
    email: z.string().trim().min(1, t('general.email_required')).email(t('input.invalid_email')),
    // isValidUserPassword mirrors check_password_policy 1:1 (length, lower, upper,
    // digit, symbol), so the request is not sent just to bounce back with a 400.
    password: z
      .string()
      .min(1, t('general.initial_password_required'))
      .refine(value => isValidUserPassword(value), { message: t('settings.password_error_info') }),
  });

export type ProfileStepValues = z.infer<ReturnType<typeof createProfileStepSchema>>;
export type CredentialsStepValues = z.infer<ReturnType<typeof createCredentialsStepSchema>>;
